import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/base/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import PlayerBox from "@/pages/detail/components/PlayerBox";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import { getMovieById, getRelated, formatViews } from "@/utils/movies";
import { useFavorites } from "@/hooks/useFavorites";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id || "");

  useDocumentMeta(
    movie ? `${movie.title} - 影视详情 - 爱看视频` : "影视详情 - 爱看视频",
    movie
      ? `在爱看视频观看${movie.title}，了解剧情简介、主演信息及更多影视资料。`
      : "在爱看视频查看影视详情、剧情简介与主演信息。"
  );

  const { isFavorite, toggle } = useFavorites();
  const [episode, setEpisode] = useState(1);
  const [toast, setToast] = useState("");

  useEffect(() => {
    setEpisode(1);
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!movie) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1">
          <EmptyState
            icon="ri-film-line"
            title="该影视内容不存在或已下架"
            description="您访问的影视可能已被移除，去看看其他精彩内容吧"
          />
          <div className="flex justify-center pb-16">
            <Link
              to="/"
              className="px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const favorited = isFavorite(movie.id);
  const related = getRelated(movie, 6);
  const isSeries = movie.episodes > 1;
  const episodeLabel = isSeries ? `第${episode}集` : "正片";

  const handleShare = async () => {
    const url = `${window.location.origin}${__BASE_PATH__}detail/${movie.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制，快去分享给好友吧");
    } catch {
      setToast("复制失败，请手动复制浏览器地址");
    }
  };

  const metaItems = [
    { label: "类型", value: movie.typeName },
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.actors.join("、") },
    { label: "年份", value: `${movie.year}` },
    { label: "地区", value: movie.region },
    { label: "更新", value: movie.updatedAt },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 pt-6">
          <nav className="flex items-center flex-wrap gap-1.5 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-base text-foreground-300"></i>
            <Link
              to={`/category/${movie.type}`}
              className="hover:text-primary-600 transition-colors"
            >
              {movie.typeName}
            </Link>
            <i className="ri-arrow-right-s-line text-base text-foreground-300"></i>
            <span className="text-foreground-800 font-medium line-clamp-1">{movie.title}</span>
          </nav>

          <section className="mt-5 rounded-lg bg-background-100/60 border border-background-200 p-5 md:p-7">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="w-40 md:w-56 shrink-0 mx-auto md:mx-0">
                <div className="w-full aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={movie.cover}
                    alt={`${movie.title} 海报`}
                    title={`${movie.title} - ${movie.typeName}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium">
                    {movie.typeName}
                  </span>
                  {movie.hot ? (
                    <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-700 text-xs font-medium">
                      热播中
                    </span>
                  ) : null}
                  <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs font-medium">
                    {isSeries ? `更新至${movie.episodes}集` : "已完结"}
                  </span>
                </div>

                <h1 className="mt-3 font-heading text-2xl md:text-3xl font-black text-foreground-950">
                  {movie.title}
                </h1>

                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                  <span className="flex items-center gap-1 font-bold text-primary-600">
                    <i className="ri-star-fill"></i>
                    {movie.rating.toFixed(1)}
                    <span className="text-foreground-400 font-normal">分</span>
                  </span>
                  <span className="flex items-center gap-1 text-foreground-500">
                    <i className="ri-fire-line text-primary-500"></i>
                    {formatViews(movie.views)}次观看
                  </span>
                  <span className="flex items-center gap-1 text-foreground-500">
                    <i className="ri-movie-2-line"></i>
                    {movie.episodes}集
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                  {metaItems.map((item) => (
                    <div key={item.label} className="flex gap-2 text-sm">
                      <span className="text-foreground-400 shrink-0 w-10">{item.label}</span>
                      <span className="text-foreground-800 line-clamp-1">{item.value}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {movie.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/search?q=${encodeURIComponent(genre)}`}
                      className="px-2.5 py-1 rounded-md bg-background-50 border border-background-200 text-xs text-foreground-600 hover:text-primary-600 hover:border-primary-300 cursor-pointer transition-colors"
                    >
                      {genre}
                    </Link>
                  ))}
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setToast("已开始播放（演示）")}
                    className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                  >
                    <i className="ri-play-fill mr-1"></i>立即播放
                  </button>
                  <button
                    type="button"
                    onClick={() => toggle(movie.id)}
                    className={`px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer border transition-colors ${
                      favorited
                        ? "bg-accent-500 border-accent-500 text-background-50"
                        : "bg-background-50 border-background-300 text-foreground-800 hover:border-accent-400 hover:text-accent-600"
                    }`}
                  >
                    <i className={`${favorited ? "ri-heart-fill" : "ri-heart-line"} mr-1`}></i>
                    {favorited ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="px-5 py-2.5 rounded-md bg-background-50 border border-background-300 text-foreground-800 text-sm font-medium whitespace-nowrap cursor-pointer hover:border-primary-400 hover:text-primary-600 transition-colors"
                  >
                    <i className="ri-share-forward-line mr-1"></i>分享
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <PlayerBox title={movie.title} episodeLabel={episodeLabel} cover={movie.cover} />

              <div className="rounded-lg bg-background-100/60 border border-background-200 p-4 md:p-5">
                <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  {movie.description}
                </p>
              </div>
            </div>

            <div className="lg:col-span-1">
              {isSeries ? (
                <EpisodeList
                  total={movie.episodes}
                  current={episode}
                  onSelect={setEpisode}
                  title={movie.title}
                />
              ) : (
                <div className="rounded-lg bg-background-100/70 border border-background-200 p-5">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">影片信息</h2>
                  <ul className="mt-4 space-y-3 text-sm">
                    <li className="flex justify-between gap-3">
                      <span className="text-foreground-400">片长</span>
                      <span className="text-foreground-800">约 120 分钟</span>
                    </li>
                    <li className="flex justify-between gap-3">
                      <span className="text-foreground-400">语言</span>
                      <span className="text-foreground-800">普通话</span>
                    </li>
                    <li className="flex justify-between gap-3">
                      <span className="text-foreground-400">清晰度</span>
                      <span className="text-foreground-800">高清 / 蓝光</span>
                    </li>
                    <li className="flex justify-between gap-3">
                      <span className="text-foreground-400">上线日期</span>
                      <span className="text-foreground-800">{movie.updatedAt}</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </section>

          <section className="mt-10 pb-4">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-1.5 h-6 rounded-full bg-accent-500" />
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">
                相关推荐
              </h2>
            </div>
            {related.length === 0 ? (
              <EmptyState title="暂无相关内容" description="暂时没有找到相似的影视推荐" />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {related.map((item) => (
                  <MovieCard key={item.id} movie={item} className="animate-fade-up" />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
      <SiteFooter />

      {toast ? (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] px-5 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm animate-fade-up">
          {toast}
        </div>
      ) : null}
    </div>
  );
}